import { Check, ChevronDown, CircleDot, Clock, Loader2, Play } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { CompileJob, CompileResult } from "../lib/api";
import { timeAgo } from "../lib/app";
import { Popover } from "./Popover";
import "./compile.css";

export const PHASES: Record<CompileJob["phase"], string> = {
  queued: "Waiting for the compile above",
  preparing: "Copying files",
  waiting: "Waiting for other projects' compiles",
  typesetting: "Typesetting",
  finishing: "Saving the PDF",
};

/** The button's label for each state. They share one grid cell, so the button keeps its width. */
export const LABELS = { idle: "Recompile", compiling: "Compiling…", waiting: "Waiting…", stopping: "Stopping…" };

export const clock = (ms: number) => {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

export const describe = (j: { draft: boolean; only: string | null; force?: boolean }) =>
  [j.only ? `Chapter with ${j.only.split("/").pop()}` : "Whole document", j.draft && "draft", j.force && "from scratch"].filter(Boolean).join(" · ");

/**
 * The compiles someone sees: the server only sends their own and full ones, and someone
 * working in draft or chapter mode isn't shown full compiles either. One of theirs stuck
 * behind a compile left out says so instead of "Starting".
 */
export function jobsFor(jobs: CompileJob[], fullMode: boolean): CompileJob[] {
  let hidden = false;
  const out: CompileJob[] = [];
  for (const job of jobs) {
    if (job.shared && !fullMode) hidden = true;
    else out.push(hidden && job.startedAt === null ? { ...job, behindOthers: true } : job);
  }
  return out;
}

/** Re-render every second while on. */
function useTicking(on: boolean) {
  const [, setN] = useState(0);
  useEffect(() => {
    if (!on) return;
    const t = window.setInterval(() => setN((n) => n + 1), 1000);
    return () => window.clearInterval(t);
  }, [on]);
}

export interface CompileButtonProps {
  canEdit: boolean;
  /** The project's waiting and running compiles, oldest first: full compiles, and this person's own draft and chapter ones. */
  jobs: CompileJob[];
  /** A compile request from this tab hasn't been answered yet. */
  requesting: boolean;
  /** The compile whose PDF (or log) is on screen. */
  shown: CompileResult | null;
  /** When the project's files last changed. */
  editedAt: number | null;
  me: string;
  autoCompile: boolean;
  draft: boolean;
  only: boolean;
  onCompile: (force?: boolean) => void;
  onStop: (job: CompileJob) => void;
  /** Stop a compile and start it again, with the edits made since it started. */
  onRestart: (job: CompileJob) => void;
  onSettings: (patch: { autoCompile?: boolean; compileDraft?: boolean; compileOnlyChapter?: boolean }) => void;
  onClearCache: () => void;
  onWordCount: () => void;
  onWordProgress: () => void;
  onDownloadSource: () => void;
}

/**
 * Recompile, as a split button. While compiles run it says what they're doing, and a click
 * opens the list of them, where each can be stopped. A dot means the PDF won't have the
 * latest changes, or the compile mode was changed since it was made.
 */
export function CompileButton(props: CompileButtonProps) {
  const { canEdit, jobs, requesting, shown, editedAt, draft, only } = props;
  const group = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const busy = requesting || jobs.length > 0;
  useTicking(busy || open);

  const running = jobs.find((j) => j.startedAt !== null);
  const state: keyof typeof LABELS = !busy
    ? "idle"
    : running?.stoppedBy
      ? "stopping"
      : running?.phase === "waiting" || (!running && jobs.length > 0)
        ? "waiting"
        : "compiling";

  // What the PDF will be once the compiles finish: the last one in line, or the one on screen.
  const last = jobs[jobs.length - 1];
  const changedSince = (snapshot: number | null | undefined) => !!snapshot && !!editedAt && editedAt > snapshot;
  const modeChanged = !!shown && (shown.draft !== draft || shown.onlyRequested !== only) && !jobs.some((j) => j.draft === draft && !!j.only === only);
  const stale = last ? last.startedAt !== null && changedSince(last.snapshotAt) : !!shown && (changedSince(shown.snapshotAt) || modeChanged);

  const title = !canEdit
    ? "Only editors can compile"
    : busy
      ? `${running ? `${describe(running)}: ${running.stoppedBy ? "stopping" : PHASES[running.phase].toLowerCase()}, ${clock(Date.now() - running.startedAt!)}` : jobs[0]?.behindOthers ? "Waiting for someone else's compile" : "Starting a compile"}${jobs.length > 1 ? `, and ${jobs.length - 1} more waiting` : ""}. Click to see or stop it.`
      : stale
        ? "Recompile (Ctrl/⌘+S): the PDF doesn't have the latest changes"
        : "Recompile (Ctrl/⌘+S)";

  const close = () => setOpen(false);
  const act = (fn: () => void) => () => {
    close();
    fn();
  };

  return (
    <>
      <div ref={group} className={`split-btn compile-btn${busy ? " compiling" : ""}`} aria-busy={busy}>
        <button
          className="btn btn-primary"
          disabled={!canEdit}
          onClick={() => (busy ? setOpen(!open) : props.onCompile())}
          title={title}
          aria-haspopup={busy ? "dialog" : undefined}
          aria-expanded={busy ? open : undefined}
        >
          <Play size={14} />
          <span className="compile-label">
            {Object.entries(LABELS).map(([k, label]) => (
              <span key={k} className={k === state ? "on" : undefined}>
                {label}
              </span>
            ))}
          </span>
          {stale && <span className="stale-dot" aria-label="Out of date" />}
        </button>
        <button className="btn btn-primary" disabled={!canEdit} onClick={() => setOpen(!open)} title="Compiles and compile options" aria-haspopup="dialog" aria-expanded={open}>
          {jobs.length > 1 && <span className="job-count">{jobs.length}</span>}
          <ChevronDown size={14} />
        </button>
      </div>
      {open && group.current && (
        <Popover anchor={group.current} className="compile-pop" label="Compiles" onClose={close}>
          <div className="cp-heading">{busy ? "Compiling" : "Compiles"}</div>
          {jobs.length === 0 && (
            <div className="cp-empty">{requesting ? "Starting a compile…" : "Nothing is compiling."}</div>
          )}
          {jobs.map((job, i) => {
            const started = job.startedAt !== null;
            const behind = changedSince(job.snapshotAt);
            return (
              <div key={job.id} className={`cp-job${started ? " running" : ""}`}>
                <span className="cp-job-icon">{started ? <Loader2 size={14} className="cp-spin" /> : <Clock size={14} />}</span>
                <div className="cp-job-body">
                  <div className="cp-job-title">{describe(job)}</div>
                  <div className="cp-job-meta">
                    {job.stoppedBy ? "Stopping…" : i === 0 && job.behindOthers ? "Waiting for someone else's compile" : i === 0 && !started ? "Starting" : PHASES[job.phase]}
                    {" · "}
                    <span className="cp-clock">{clock(Date.now() - (job.startedAt ?? job.requestedAt))}</span>
                    {job.by && job.by !== props.me && ` · ${job.by}`}
                  </div>
                  {started && behind && !job.stoppedBy && (
                    <div className="cp-job-behind">
                      Edited since it started, so it won't have those changes.
                      <button className="cp-link" onClick={act(() => props.onRestart(job))}>
                        Restart with them
                      </button>
                    </div>
                  )}
                </div>
                {/* Once it's saving the PDF there's nothing left worth stopping. */}
                {job.phase !== "finishing" && (
                  <button className="btn btn-sm cp-stop" disabled={!!job.stoppedBy} onClick={() => props.onStop(job)}>
                    {started ? "Stop" : "Cancel"}
                  </button>
                )}
              </div>
            );
          })}
          {busy && (
            <button className="menu-item" onClick={act(() => props.onCompile())} title="Starts when the compiles above finish, with every change made by then">
              <span className="menu-icon">
                <Play size={14} />
              </span>
              Recompile after {jobs.length > 1 ? "these" : "this"}
            </button>
          )}
          {!shown ? (
            <div className="cp-pdf">No PDF yet.</div>
          ) : (
            <>
              {changedSince(shown.snapshotAt) ? (
                <div className="cp-pdf">
                  <CircleDot size={13} className="cp-warn" />
                  <span>The PDF on screen is from {timeAgo(shown.startedAt)}. The project has changed since.</span>
                </div>
              ) : (
                !modeChanged && (
                  <div className="cp-pdf">
                    <Check size={13} className="cp-ok" />
                    <span>The PDF on screen is up to date.</span>
                  </div>
                )
              )}
              {modeChanged && (
                <div className="cp-pdf">
                  <CircleDot size={13} className="cp-warn" />
                  <span>
                    The PDF on screen was compiled as {describe({ draft: shown.draft, only: shown.onlyRequested ? shown.only : null }).toLowerCase()}. The new mode
                    applies when you next recompile.
                  </span>
                </div>
              )}
            </>
          )}
          <div className="menu-sep" />
          <Toggle label="Auto-compile" on={props.autoCompile} onToggle={() => props.onSettings({ autoCompile: !props.autoCompile })} />
          <Toggle label="Draft mode: images as boxes" hint="fast" on={draft} onToggle={() => props.onSettings({ compileDraft: !draft })} />
          <Toggle label="Only the chapter I'm editing" hint="\includeonly" on={only} onToggle={() => props.onSettings({ compileOnlyChapter: !only })} />
          {(draft || only) && <div className="cp-note">In this mode your compiles are just yours, and other people's full compiles don't change your PDF.</div>}
          <div className="menu-sep" />
          <button className="menu-item" onClick={act(() => props.onCompile(true))}>
            Recompile from scratch
          </button>
          <button className="menu-item" onClick={act(props.onClearCache)}>
            Clear compile cache
          </button>
          <div className="menu-sep" />
          <button className="menu-item" onClick={act(props.onWordCount)}>
            Word count…
          </button>
          <button className="menu-item" onClick={act(props.onWordProgress)}>
            Word progress
          </button>
          <button className="menu-item" onClick={act(props.onDownloadSource)}>
            Download source (.zip)
          </button>
        </Popover>
      )}
    </>
  );
}

/** A compile option. Changing one doesn't compile; it applies from the next compile. */
export function Toggle({ label, hint, on, onToggle }: { label: string; hint?: string; on: boolean; onToggle: () => void }) {
  return (
    <button className={`menu-item cp-toggle${on ? " checked" : ""}`} role="menuitemcheckbox" aria-checked={on} onClick={onToggle}>
      {/* Drawn like the app's checkboxes: an input can't go inside a button. */}
      <span className="cp-box" aria-hidden>
        {on && <Check size={11} strokeWidth={3} />}
      </span>
      {label}
      {hint && <kbd>{hint}</kbd>}
    </button>
  );
}
